import {
  decodeFunctionData,
  parseAbi,
  type Abi,
} from 'viem';
import type { DecodedField } from '@txviz/core';
import { sanitizeDisplayString } from '@txviz/core';
import { ERC20_SELECTORS, ERC721_SELECTORS, ERC1155_SELECTORS } from './types.js';

/**
 * All well-known standard ABIs bundled with the library.
 * These are trusted and don't need a "unverified" warning.
 */
const STANDARD_ABIS = parseAbi([
  // ERC-20
  'function transfer(address to, uint256 amount)',
  'function transferFrom(address from, address to, uint256 amount)',
  'function approve(address spender, uint256 amount)',
  // ERC-721
  'function safeTransferFrom(address from, address to, uint256 tokenId)',
  'function safeTransferFrom(address from, address to, uint256 tokenId, bytes data)',
  'function setApprovalForAll(address operator, bool approved)',
  // ERC-1155
  'function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes data)',
  'function safeBatchTransferFrom(address from, address to, uint256[] ids, uint256[] amounts, bytes data)',
]);

interface DecodedCalldata {
  functionName: string;
  args: readonly unknown[];
  signature: string;
  fields: DecodedField[];
  isFromStandardAbi: boolean;
}

/**
 * Attempt to decode calldata using:
 * 1. User-provided ABI (trusted)
 * 2. Built-in standard ABIs (trusted)
 * 3. 4byte.directory lookup (untrusted — marked with warning)
 */
export async function decodeCalldata(
  data: string,
  userAbi?: readonly unknown[],
): Promise<DecodedCalldata | null> {
  if (data === '0x' || data.length < 10) {
    return null;
  }

  const selector = data.slice(0, 10).toLowerCase();

  // Try user-provided ABI first (most trusted)
  if (userAbi && userAbi.length > 0) {
    try {
      const result = decodeFunctionData({
        abi: userAbi as Abi,
        data: data as `0x${string}`,
      });
      return {
        functionName: result.functionName,
        args: result.args ?? [],
        signature: `${result.functionName}(...)`,
        fields: formatArgs(result.functionName, result.args ?? []),
        isFromStandardAbi: false,
      };
    } catch {
      // ABI doesn't match this calldata, continue to fallbacks
    }
  }

  // Try standard ABIs (ERC-20/721/1155)
  try {
    const result = decodeFunctionData({
      abi: STANDARD_ABIS,
      data: data as `0x${string}`,
    });

    const standard = getStandardForSelector(selector);
    return {
      functionName: result.functionName,
      args: result.args ?? [],
      signature: `${result.functionName}(...)`,
      fields: formatArgs(result.functionName, result.args ?? [], standard),
      isFromStandardAbi: true,
    };
  } catch {
    // Not a standard function
  }

  // Try 4byte.directory lookup (untrusted)
  try {
    const signature = await lookup4byte(selector);
    if (signature) {
      return {
        functionName: signature.split('(')[0] ?? selector,
        args: [],
        signature,
        fields: [
          {
            label: 'Function',
            value: sanitizeDisplayString(signature),
            rawValue: selector,
            type: 'text' as const,
            warning: 'Function signature from public registry (4byte.directory) — verify independently',
          },
          {
            label: 'Raw Calldata',
            value: data.length > 74 ? `${data.slice(0, 74)}...` : data,
            rawValue: data,
            type: 'data' as const,
          },
        ],
        isFromStandardAbi: false,
      };
    }
  } catch {
    // Network error or 4byte unavailable
  }

  // Could not decode
  return null;
}

/**
 * Look up a function selector on 4byte.directory.
 * Returns the most popular matching signature, or null.
 */
async function lookup4byte(selector: string): Promise<string | null> {
  try {
    const response = await fetch(
      `https://www.4byte.directory/api/v1/signatures/?hex_signature=${selector}&ordering=-created_at`,
      {
        signal: AbortSignal.timeout(5000),
        headers: { 'Accept': 'application/json' },
      },
    );

    if (!response.ok) return null;

    const data = await response.json() as {
      results?: { text_signature: string }[];
    };

    if (data.results && data.results.length > 0) {
      const first = data.results[0];
      return first ? first.text_signature : null;
    }
  } catch {
    // Network error or timeout — silently fail
  }

  return null;
}

function getStandardForSelector(selector: string): string | undefined {
  if (ERC20_SELECTORS[selector]) return 'ERC-20';
  if (ERC721_SELECTORS[selector]) return 'ERC-721';
  if (ERC1155_SELECTORS[selector]) return 'ERC-1155';
  return undefined;
}

function formatArgs(
  functionName: string,
  args: readonly unknown[],
  standard?: string,
): DecodedField[] {
  const fields: DecodedField[] = [];
  const prefix = standard ? `${standard} ` : '';

  fields.push({
    label: 'Function',
    value: `${prefix}${sanitizeDisplayString(functionName)}`,
    rawValue: functionName,
    type: 'text',
  });

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    const argStr = typeof arg === 'bigint' ? arg.toString() : String(arg);
    fields.push({
      label: `Arg ${i}`,
      value: sanitizeDisplayString(argStr),
      rawValue: argStr,
      type: inferArgType(arg),
    });
  }

  return fields;
}

function inferArgType(arg: unknown): 'address' | 'value' | 'data' | 'number' | 'text' {
  if (typeof arg === 'bigint') return 'number';
  if (typeof arg === 'string' && arg.startsWith('0x') && arg.length === 42) return 'address';
  if (typeof arg === 'string' && arg.startsWith('0x')) return 'data';
  if (typeof arg === 'boolean') return 'text';
  return 'text';
}
